import I18n from 'i18n-js';
import { citationsLandingPluralCategory } from 'js/citations_landing/citationsLandingPluralCategory';
import { isRtlLocale } from 'js/utils/rtl';

/**
 * I18n.t, isolating interpolated values.
 *
 * Prices and counts arrive Latin-script ("$99"). Dropped raw into an RTL
 * sentence the bidi algorithm reorders them against neighbouring punctuation,
 * so "$99" renders as "99$". Wrapping each in FSI/PDI resolves its direction
 * without leaking into the sentence.
 */

const FSI = '⁨';
const PDI = '⁩';

// `count` drives pluralization and must stay a bare number; its display twin
// `count_delimited` carries the formatted text and does get isolated.
const NEVER_ISOLATE = new Set(['count']);

// Scalars only: I18n.t stringifies whatever it's given, so a React element here
// would render as "[object Object]". undefined is fine for a key without that slot.
export type TranslateValues = Record<string, string | number | undefined>;

function isolate(values: TranslateValues): TranslateValues {
  const out: TranslateValues = {};
  for (const [key, value] of Object.entries(values)) {
    const isolatable =
      !NEVER_ISOLATE.has(key) &&
      (typeof value === 'string' || typeof value === 'number');
    out[key] = isolatable ? `${FSI}${value}${PDI}` : value;
  }
  return out;
}

export function isolatedTranslate(
  key: string,
  values?: TranslateValues,
): string {
  if (!values || !isRtlLocale(I18n.currentLocale())) return I18n.t(key, values);

  return I18n.t(key, isolate(values));
}

/**
 * isolatedTranslate for a counted key ({one:, few:, other:, ...} in en.yml). i18n-js's own
 * pluralizer is locale-blind (count === 1 -> 'one', else 'other'), so ru/pl/uk/ar would read the
 * wrong form for 2-4; this picks the CLDR category itself. `count` is the raw number -- pass its
 * display form (e.g. formatStat(count)) as its own placeholder.
 */
export function isolatedTranslatePlural(
  key: string,
  count: number,
  values?: TranslateValues,
): string {
  const category = citationsLandingPluralCategory(I18n.currentLocale(), count);

  return isolatedTranslate(`${key}.${category}`, { count, ...values });
}

function has(lang: string, key: string): boolean {
  let node: unknown = I18n.translations[lang];
  for (const part of key.split('.')) {
    if (typeof node !== 'object' || node === null) return false;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string';
}

export interface PageTranslation {
  t: (key: string, values?: TranslateValues) => string;
  tPlural: (key: string, count: number, values?: TranslateValues) => string;
  applyLang: (lang: string) => Promise<void>;
}

/**
 * Translation calls for one page, in a lang only this page's copy follows: the global I18n.locale,
 * which every other bundle on the page reads, is left alone. A key the lang lacks (a declined
 * plural category, a lang whose run didn't finish) falls back to its other form, then English.
 *
 * Client-rendered pages only: the lang is module state, which a server renderer shares across
 * requests, so a server-rendered page needs the lang passed per render instead.
 */
export function pageTranslation(
  loaders: Partial<Record<string, () => Promise<unknown>>>,
): PageTranslation {
  let current = 'en';
  // Only the latest applyLang commits, so a slow chunk can't overwrite a later choice.
  let latest = 0;

  const translateIn = (
    lang: string,
    key: string,
    values?: TranslateValues,
  ): string => {
    const shown = values && isRtlLocale(lang) ? isolate(values) : values;
    return I18n.t(key, { ...shown, locale: lang });
  };

  return {
    t: (key, values) =>
      translateIn(has(current, key) ? current : 'en', key, values),
    tPlural: (key, count, values) => {
      // The lang first, then its category: English's own forms if the lang lacks the key.
      const lang = has(current, `${key}.other`) ? current : 'en';
      const category = citationsLandingPluralCategory(lang, count);
      const form = has(lang, `${key}.${category}`) ? category : 'other';
      return translateIn(lang, `${key}.${form}`, { count, ...values });
    },
    // Loads one lang's bundle and switches this page to it; 'en' or a lang the page doesn't ship
    // switches it back to English.
    applyLang: async (lang) => {
      const call = ++latest;
      const load = loaders[lang];
      if (load) await load();
      if (call === latest) current = load ? lang : 'en';
    },
  };
}
